export const AGGREGATION_OPTIONS = [
  'android_id',
  'asn_number',
  'asn_org',
  'channel',
  'concept',
  'domain',
  'estid',
  'geo_postal',
  'geo_dma',
  'geo_iso',
  'geo_subISO',
  'hem_md5',
  'hem_sha1',
  'hem_sha256',
  'ios_id',
  'ip',
  'mappedEvent',
  'ttd_id',
  'url',
]

export const AGGREGATION_OPTIONS_NOT_SUPPORTED_BY_DATA_PREVIEW = ['concept']

export const AGGREGATION_SUPPORTED_PREFIXES = [
  'std',
  'cpg',
  'b2b',
  'auto',
  'baf',
  'finance',
  'health',
  ...AGGREGATION_OPTIONS,
]

// Checks if attribute starts with one of the supported prefixes for aggregation
export const doesAttributeSupportAggregation = (attribute) =>
  AGGREGATION_SUPPORTED_PREFIXES.some((prefix) => attribute.startsWith(prefix))

// The Custom Label "Feed Output Root" is stored as a full S3 path; the user only
// edits the trailing name segment. These helpers convert between the two so the
// name is the single source of truth and clearing the field yields an empty name
// (rather than resurrecting a structural path segment as a phantom default).
export const FEED_OUTPUT_ROOT_PREFIX = 's3://sharethis-daas-feeds/feeds/custom-label/'

export const sanitizeFeedOutputSegment = (value) => {
  if (!value && value !== 0) return ''
  return String(value)
    .trim()
    .replace(/[^a-zA-Z0-9]/g, '-')
}

export const buildFeedOutputRoot = (name) =>
  `${FEED_OUTPUT_ROOT_PREFIX}${sanitizeFeedOutputSegment(name)}/`

export const getFeedOutputRootName = (feedOutputRoot) => {
  if (typeof feedOutputRoot === 'string' && feedOutputRoot.startsWith(FEED_OUTPUT_ROOT_PREFIX)) {
    return feedOutputRoot.slice(FEED_OUTPUT_ROOT_PREFIX.length).replace(/\/+$/, '')
  }
  return ''
}

// A taxonomy is "selected" only when it holds an actual value. When selected it
// is a string id; when unselected it is undefined/null, an empty array (the
// "Remove all" action resets it to []), or an empty object (the API returns
// custom_taxonomy: {} for feeds without one). Boolean([]) and Boolean({}) are
// both truthy, so guard for the empty cases explicitly — otherwise the
// dependent required fields (e.g. Topics Threshold) would be enforced with no
// taxonomy chosen.
export const isCustomTaxonomySelected = (data) => {
  const value = data?.customTaxonomy
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0
  return Boolean(value)
}

// The Custom Label toggle is on only while the payload actually holds a
// configuration object. Turning it off clears it to null (the API's schema
// default), and feeds that never used it come back as null or '' from the API.
export const isCustomLabelEnabled = (data) => {
  const value = data?.customLabel
  return typeof value === 'object' && value !== null && Object.keys(value).length > 0
}

// Clears every Custom Label attribute from the feed payload. Sending null (not
// omitting the key) is what tells the API to drop the stored attributes.
export const clearCustomLabel = (data) => ({ ...data, customLabel: null })

// Validates the page-level Custom Taxonomy required fields. These live outside
// the delivery modal, so they must be checked before the modal can be opened.
// Returns a map of field -> boolean (true means missing/invalid).
export const getTaxonomyRequiredErrors = (data) => {
  const customTaxonomySelected = isCustomTaxonomySelected(data)
  const customLabelEnabled = isCustomLabelEnabled(data)

  return {
    topicsThreshold:
      customTaxonomySelected &&
      !(
        typeof data?.topicsThreshold === 'number' &&
        data.topicsThreshold >= 0 &&
        data.topicsThreshold <= 1
      ),
    customLabelKeccField:
      customTaxonomySelected && customLabelEnabled && !data?.customLabel?.keccField,
    customLabelKeccThreshold:
      customTaxonomySelected &&
      customLabelEnabled &&
      !(
        typeof data?.customLabel?.keccThreshold === 'number' &&
        data.customLabel.keccThreshold >= 0 &&
        data.customLabel.keccThreshold <= 1
      ),
    customLabelFeedOutputRoot:
      customTaxonomySelected &&
      customLabelEnabled &&
      !getFeedOutputRootName(data?.customLabel?.feedOutputRoot),
  }
}

export const hasTaxonomyRequiredErrors = (data) =>
  Object.values(getTaxonomyRequiredErrors(data)).some(Boolean)
